import { ChangeEvent, DragEvent, FormEvent, useEffect, useMemo, useState } from "react";
import { CheckCircle2, ChevronDown, ChevronRight, ExternalLink, FolderOpen, RefreshCw, Search, Trash2, UploadCloud } from "lucide-react";
import { azureSqlService } from "@/services/azureSqlService.js";
import RecentAccessLogPane from "@/components/accessLogs/RecentAccessLogPane";
import {
  projectFilesService,
  type ProjectFilePackageType,
  type ProjectFileRecord,
  type ProjectFileUploadResult,
} from "@/services/projectFilesService";
import type { AdminAccessLogRecord } from "@/types/accessLogs";
import { Button, Card, CardHeader, EmptyState, IconButton, StatusBadge } from "@/ui/components";
import {
  extractProjectNumberFromProjectFileName,
  extractRevisionDateFromProjectFileName,
} from "@/utils/projectFileNameParsing";

export {
  extractProjectNumberFromProjectFileName,
  extractRevisionDateFromProjectFileName,
};

type ProjectOption = {
  projectId: number;
  projectNumber: string;
  projectName: string;
  siteAddress: string;
  suburb: string;
  postCode: string;
};

type UploadStage = "idle" | "searching" | "loading" | "uploading" | "registered";
type InventoryScope = "all" | "project";

const PACKAGE_OPTIONS: Array<{ value: ProjectFilePackageType; label: string }> = [
  { value: "structural", label: "Structural package" },
  { value: "civil", label: "Civil package" },
  { value: "other", label: "Other project files" },
];

function todayInputValue() {
  return new Date().toISOString().slice(0, 10);
}

function normalizeProjectRows(response: unknown): ProjectOption[] {
  const rows = Array.isArray(response)
    ? response
    : response && typeof response === "object" && Array.isArray((response as any).data)
      ? (response as any).data
      : [];

  return rows.flatMap((project: any) => {
    const projectId = Number(project?.ProjectId ?? project?.Id);
    if (!Number.isFinite(projectId) || projectId <= 0) return [];
    return [{
      projectId,
      projectNumber: String(project?.ProjectCode || project?.ProjectNumber || "").trim(),
      projectName: String(project?.ProjectName || project?.Title || "Unnamed project").trim(),
      siteAddress: String(project?.SiteAddress || project?.Address || "").trim(),
      suburb: String(project?.Suburb || "").trim(),
      postCode: String(project?.PostCode ?? project?.Postcode ?? "").trim(),
    }];
  });
}

function normalizeComparable(value: unknown) {
  return String(value || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function extractProjectNumberFromSearchInput(value: string): string {
  const match = String(value || "").match(/\b\d{4,6}\b/);
  return match?.[0] || "";
}

function formatFileSize(bytes?: number | null) {
  const value = Number(bytes || 0);
  if (!Number.isFinite(value) || value <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let next = value;
  let unitIndex = 0;
  while (next >= 1024 && unitIndex < units.length - 1) {
    next /= 1024;
    unitIndex += 1;
  }
  return `${next.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function formatDate(value?: string | null) {
  if (!value) return "No date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value.slice(0, 10);
  return date.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
}

function projectTitle(project: ProjectOption) {
  return project.projectNumber || `Project ${project.projectId}`;
}

function projectAddress(project: ProjectOption) {
  return [project.siteAddress, project.suburb, project.postCode].filter(Boolean).join(", ") || "No saved address";
}

function getUploadStageLabel(stage: UploadStage) {
  switch (stage) {
    case "searching":
      return "Searching";
    case "loading":
      return "Loading";
    case "uploading":
      return "Uploading";
    case "registered":
      return "Registered";
    default:
      return "Ready";
  }
}

type UploadFileRevision = {
  file: File;
  revisionDate: string;
  detectedFromFileName: boolean;
};

type ProjectFileGroup = {
  key: string;
  title: string;
  address: string;
  files: ProjectFileRecord[];
};

function buildUploadFileRevisions(files: File[], fallbackRevisionDate: string): UploadFileRevision[] {
  return files.map((file) => {
    const detectedRevisionDate = extractRevisionDateFromProjectFileName(file.name);
    return {
      file,
      revisionDate: detectedRevisionDate || fallbackRevisionDate,
      detectedFromFileName: Boolean(detectedRevisionDate),
    };
  });
}

function buildUploadConfirmationMessage(
  project: ProjectOption,
  packageLabel: string,
  uploadFiles: UploadFileRevision[]
): string {
  const visibleFiles = uploadFiles.slice(0, 8);
  const fileLines = visibleFiles.map(({ file, revisionDate, detectedFromFileName }) => (
    `- ${file.name}: ${formatDate(revisionDate)}${detectedFromFileName ? " (from file name)" : " (manual field)"}`
  ));
  if (uploadFiles.length > visibleFiles.length) {
    fileLines.push(`- ${uploadFiles.length - visibleFiles.length} more file(s)`);
  }

  return [
    "Confirm project file upload",
    "",
    `Project: ${projectTitle(project)} - ${projectAddress(project)}`,
    `Package: ${packageLabel}`,
    `Files: ${uploadFiles.length}`,
    "",
    "Revision date to save:",
    ...fileLines,
    "",
    "Continue upload?",
  ].join("\n");
}

function projectFileGroupTitle(file: ProjectFileRecord): string {
  return file.projectNumber || `Project ${file.projectId}`;
}

function projectFileGroupAddress(file: ProjectFileRecord): string {
  return [file.projectAddress, file.projectSuburb].filter(Boolean).join(", ") || "No saved address";
}

function groupProjectFiles(files: ProjectFileRecord[]): ProjectFileGroup[] {
  const groups = new Map<string, ProjectFileGroup>();

  for (const file of files) {
    const key = String(file.projectId || file.projectNumber || "unknown");
    const existing = groups.get(key);
    if (existing) {
      existing.files.push(file);
      continue;
    }

    groups.set(key, {
      key,
      title: projectFileGroupTitle(file),
      address: projectFileGroupAddress(file),
      files: [file],
    });
  }

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      files: group.files.slice().sort((left, right) => {
        const dateCompare = String(right.revisionDate || "").localeCompare(String(left.revisionDate || ""));
        return dateCompare || String(right.createdAt || "").localeCompare(String(left.createdAt || ""));
      }),
    }))
    .sort((left, right) => left.title.localeCompare(right.title, undefined, { numeric: true, sensitivity: "base" }));
}

function countRevisions(files: ProjectFileRecord[]): number {
  return new Set(files.map((file) => file.revisionDate).filter(Boolean)).size;
}

export default function ProjectFilesAdminPanel() {
  const [projectQuery, setProjectQuery] = useState("");
  const [projectOptions, setProjectOptions] = useState<ProjectOption[]>([]);
  const [selectedProject, setSelectedProject] = useState<ProjectOption | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [guessedProjectNumber, setGuessedProjectNumber] = useState("");
  const [packageType, setPackageType] = useState<ProjectFilePackageType>("structural");
  const [revisionDate, setRevisionDate] = useState(todayInputValue);
  const [dragActive, setDragActive] = useState(false);
  const [stage, setStage] = useState<UploadStage>("idle");
  const [feedback, setFeedback] = useState<string | null>(null);
  const [feedbackTone, setFeedbackTone] = useState<"success" | "error">("success");
  const [lastUpload, setLastUpload] = useState<ProjectFileUploadResult | null>(null);
  const [registeredFiles, setRegisteredFiles] = useState<ProjectFileRecord[]>([]);
  const [inventoryScope, setInventoryScope] = useState<InventoryScope>("all");
  const [expandedInventoryProjectKeys, setExpandedInventoryProjectKeys] = useState<string[]>([]);
  const [reviewingFileId, setReviewingFileId] = useState<string | null>(null);
  const [accessLogs, setAccessLogs] = useState<AdminAccessLogRecord[]>([]);
  const [accessLogsLoading, setAccessLogsLoading] = useState(false);
  const [accessLogsError, setAccessLogsError] = useState<string | null>(null);

  const busy = stage === "searching" || stage === "loading" || stage === "uploading";
  const canUpload = useMemo(
    () => Boolean(selectedProject && selectedFiles.length && revisionDate && !busy),
    [busy, revisionDate, selectedFiles.length, selectedProject]
  );
  const detectedRevisionDate = useMemo(
    () => selectedFiles.map((file) => extractRevisionDateFromProjectFileName(file.name)).find(Boolean) || "",
    [selectedFiles]
  );
  const selectedFileMeta = useMemo(() => {
    if (!selectedFiles.length) return "";
    const parts = [
      formatFileSize(selectedFiles.reduce((total, file) => total + file.size, 0)),
    ];
    if (guessedProjectNumber) parts.push(`detected project ${guessedProjectNumber}`);
    if (detectedRevisionDate) parts.push(`detected date ${formatDate(detectedRevisionDate)}`);
    return parts.join(" · ");
  }, [detectedRevisionDate, guessedProjectNumber, selectedFiles]);
  const inventoryGroups = useMemo(() => groupProjectFiles(registeredFiles), [registeredFiles]);
  const inventoryScopeText = inventoryScope === "project" && selectedProject
    ? `${projectTitle(selectedProject)} files`
    : "Latest files across projects";

  useEffect(() => {
    const availableKeys = new Set(inventoryGroups.map((group) => group.key));
    setExpandedInventoryProjectKeys((current) => {
      const retained = current.filter((key) => availableKeys.has(key));
      if (inventoryScope === "project" && inventoryGroups.length === 1 && retained.length === 0) {
        return [inventoryGroups[0].key];
      }
      return retained;
    });
  }, [inventoryGroups, inventoryScope]);

  function toggleInventoryProject(key: string) {
    setExpandedInventoryProjectKeys((current) => (
      current.includes(key)
        ? current.filter((existingKey) => existingKey !== key)
        : [...current, key]
    ));
  }

  async function loadRegisteredFiles(
    projectId = selectedProject?.projectId || null,
    options: { scope?: InventoryScope } = {}
  ) {
    if (options.scope) {
      setInventoryScope(options.scope);
    }
    setStage((current) => current === "uploading" ? current : "loading");
    try {
      const files = await projectFilesService.listFiles(projectId);
      setRegisteredFiles(files);
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : "Project files could not be loaded.");
      setFeedbackTone("error");
    } finally {
      setStage("idle");
    }
  }

  async function loadAccessLogs() {
    setAccessLogsLoading(true);
    setAccessLogsError(null);
    try {
      setAccessLogs(await projectFilesService.listAccessLogs(30));
    } catch (error) {
      setAccessLogsError(error instanceof Error ? error.message : "Project file access logs could not be loaded.");
    } finally {
      setAccessLogsLoading(false);
    }
  }

  useEffect(() => {
    void loadRegisteredFiles(null, { scope: "all" });
    void loadAccessLogs();
  }, []);

  async function searchProjectsByQuery(query: string, options: { autoSelectExact?: boolean } = {}) {
    const trimmedQuery = query.trim();
    if (trimmedQuery.length < 2) {
      setFeedback("Enter at least 2 characters to search projects.");
      setFeedbackTone("error");
      return;
    }

    setStage("searching");
    setFeedback(null);
    setProjectOptions([]);
    try {
      const results = await azureSqlService.searchProjects(trimmedQuery, 8, { lifecycle: "all" });
      const normalized = normalizeProjectRows(results);
      setProjectOptions(normalized);

      if (options.autoSelectExact) {
        const exactMatches = normalized.filter((project) => normalizeComparable(project.projectNumber) === normalizeComparable(trimmedQuery));
        if (exactMatches.length === 1) {
          selectProject(exactMatches[0], { quiet: true });
          setFeedback(`Matched project ${projectTitle(exactMatches[0])}.`);
          setFeedbackTone("success");
          return;
        }
      }

      setFeedback(normalized.length ? `${normalized.length} matching project row(s) found.` : "No project rows matched that search.");
      setFeedbackTone(normalized.length ? "success" : "error");
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : "Project search failed.");
      setFeedbackTone("error");
    } finally {
      setStage("idle");
    }
  }

  async function searchProjects(event?: FormEvent) {
    event?.preventDefault();
    await searchProjectsByQuery(projectQuery);
  }

  function selectProject(project: ProjectOption, options: { quiet?: boolean } = {}) {
    setSelectedProject(project);
    setProjectQuery([project.projectNumber, project.projectName].filter(Boolean).join(" - "));
    void loadRegisteredFiles(project.projectId, { scope: "project" });
    if (!options.quiet) {
      setFeedback(`Selected ${projectTitle(project)}.`);
      setFeedbackTone("success");
    }
  }

  function applyFiles(fileList: FileList | File[] | null) {
    const nextFiles = Array.from(fileList || []).filter((file) => file.size >= 0);
    setLastUpload(null);
    setStage("idle");
    if (!nextFiles.length) {
      setSelectedFiles([]);
      setGuessedProjectNumber("");
      return;
    }

    const guessed = nextFiles.map((file) => extractProjectNumberFromProjectFileName(file.name)).find(Boolean) || "";
    const guessedRevisionDate = nextFiles.map((file) => extractRevisionDateFromProjectFileName(file.name)).find(Boolean) || "";
    setSelectedFiles(nextFiles);
    setGuessedProjectNumber(guessed);
    if (guessedRevisionDate) {
      setRevisionDate(guessedRevisionDate);
    }
    if (guessed) {
      setProjectQuery(guessed);
      setFeedback(`Detected project number ${guessed}.`);
      setFeedbackTone("success");
      void searchProjectsByQuery(guessed, { autoSelectExact: true });
      return;
    }

    setFeedback(guessedRevisionDate
      ? `Detected revision date ${formatDate(guessedRevisionDate)} from file name.`
      : `${nextFiles.length} file${nextFiles.length === 1 ? "" : "s"} selected.`
    );
    setFeedbackTone("success");
  }

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    applyFiles(event.target.files);
  }

  function handleDrop(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault();
    setDragActive(false);
    applyFiles(event.dataTransfer.files);
  }

  async function uploadProjectFiles() {
    if (!selectedProject || !selectedFiles.length) {
      setFeedback("Select a project and file first.");
      setFeedbackTone("error");
      return;
    }

    const packageLabel = PACKAGE_OPTIONS.find((option) => option.value === packageType)?.label || packageType;
    const uploadFiles = buildUploadFileRevisions(selectedFiles, revisionDate);
    const confirmed = window.confirm(buildUploadConfirmationMessage(selectedProject, packageLabel, uploadFiles));
    if (!confirmed) {
      setFeedback("Upload cancelled. No files were uploaded.");
      setFeedbackTone("error");
      return;
    }

    setStage("uploading");
    setFeedback(`Uploading ${selectedFiles.length} file${selectedFiles.length === 1 ? "" : "s"}...`);
    setFeedbackTone("success");
    try {
      let latest: ProjectFileUploadResult | null = null;
      for (const uploadFile of uploadFiles) {
        latest = await projectFilesService.uploadFileForProject({
          projectId: selectedProject.projectId,
          file: uploadFile.file,
          packageType,
          revisionDate: uploadFile.revisionDate,
        });
      }
      setLastUpload(latest);
      setSelectedFiles([]);
      setGuessedProjectNumber("");
      setFeedback(`Uploaded ${selectedFiles.length} file${selectedFiles.length === 1 ? "" : "s"} for ${projectTitle(selectedProject)}.`);
      setFeedbackTone("success");
      setStage("registered");
      await loadRegisteredFiles(selectedProject.projectId, { scope: "project" });
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : "Project file upload failed.");
      setFeedbackTone("error");
      setStage("idle");
    }
  }

  async function deleteFile(file: ProjectFileRecord) {
    const confirmed = window.confirm(`Delete ${file.fileName}?`);
    if (!confirmed) return;

    try {
      await projectFilesService.deleteFile(file.id);
      setFeedback(`Deleted ${file.fileName}.`);
      setFeedbackTone("success");
      await loadRegisteredFiles(
        inventoryScope === "project" ? selectedProject?.projectId || file.projectId : null,
        { scope: inventoryScope }
      );
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : "Project file could not be deleted.");
      setFeedbackTone("error");
    }
  }

  async function openFileForReview(file: ProjectFileRecord) {
    const previewWindow = window.open(
      "",
      "_blank",
      "popup=yes,width=1200,height=850,resizable=yes,scrollbars=yes"
    );
    if (!previewWindow) {
      setFeedback("Allow pop-ups for this site, then select the file again.");
      setFeedbackTone("error");
      return;
    }

    previewWindow.opener = null;
    previewWindow.document.title = `Opening ${file.fileName}`;
    previewWindow.document.body.textContent = "Preparing secure file preview...";
    setReviewingFileId(file.id);
    setFeedback(null);

    try {
      const link = await projectFilesService.createDownloadLink(file.id);
      if (!previewWindow.closed) {
        previewWindow.location.replace(link.downloadUrl);
        previewWindow.focus();
      }
    } catch (error) {
      if (!previewWindow.closed) previewWindow.close();
      setFeedback(error instanceof Error ? error.message : "Project file preview could not be opened.");
      setFeedbackTone("error");
    } finally {
      setReviewingFileId(null);
    }
  }

  const debugProjectNumber = selectedProject?.projectNumber || extractProjectNumberFromSearchInput(projectQuery);

  return (
    <Card
      className="project-files-admin-card"
      header={(
        <CardHeader
          title="Project File System"
          subtitle="Blob-backed project packages for verified client downloads."
          actions={<StatusBadge status={stage}>{getUploadStageLabel(stage)}</StatusBadge>}
        />
      )}
    >
      <div className="project-files-admin">
        <aside className="project-files-admin__inventory" aria-label="Project file inventory">
          <div className="project-files-admin__section-header">
            <div>
              <h3>File library</h3>
              <p>{inventoryScopeText}</p>
            </div>
            <StatusBadge status={String(registeredFiles.length)}>{registeredFiles.length} files</StatusBadge>
          </div>

          <div className="project-files-admin__inventory-actions">
            <Button type="button" variant="secondary" size="sm" onClick={() => loadRegisteredFiles(null, { scope: "all" })} disabled={busy}>
              <FolderOpen size={15} aria-hidden="true" />
              All files
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => loadRegisteredFiles(inventoryScope === "project" ? selectedProject?.projectId || null : null, { scope: inventoryScope })}
              disabled={busy}
            >
              <RefreshCw size={15} aria-hidden="true" />
              Refresh
            </Button>
          </div>

          <div className="project-files-admin__inventory-scroll">
            {inventoryGroups.length ? (
              <div className="project-files-admin-list">
                {inventoryGroups.map((group) => (
                  <section
                    key={group.key}
                    className={`project-files-inventory-project${expandedInventoryProjectKeys.includes(group.key) ? " project-files-inventory-project--expanded" : ""}`}
                  >
                    <button
                      type="button"
                      className="project-files-inventory-project__header"
                      onClick={() => toggleInventoryProject(group.key)}
                      aria-expanded={expandedInventoryProjectKeys.includes(group.key)}
                      aria-controls={`project-files-inventory-${group.key}`}
                    >
                      <span className="project-files-inventory-project__chevron" aria-hidden="true">
                        {expandedInventoryProjectKeys.includes(group.key) ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </span>
                      <span className="project-files-inventory-project__summary">
                        <strong>{group.title}</strong>
                        <span>{group.address}</span>
                      </span>
                      <small>{group.files.length} file{group.files.length === 1 ? "" : "s"} · {countRevisions(group.files)} rev</small>
                    </button>

                    {expandedInventoryProjectKeys.includes(group.key) ? (
                      <div id={`project-files-inventory-${group.key}`} className="project-files-inventory-project__files">
                        {group.files.map((file) => (
                          <div key={file.id} className="project-files-admin-file">
                            <button
                              type="button"
                              className="project-files-admin-file__main"
                              onClick={() => openFileForReview(file)}
                              disabled={busy || Boolean(reviewingFileId)}
                              aria-label={`Open ${file.fileName} for review`}
                            >
                              <strong>
                                {file.fileName}
                                <ExternalLink size={15} aria-hidden="true" />
                              </strong>
                              <span className="company-ai-source-strip">
                                <span className="company-ai-source-pill">{file.packageLabel}</span>
                                <span className="company-ai-source-pill">{formatDate(file.revisionDate)}</span>
                                <span className="company-ai-source-pill">{formatFileSize(file.fileSizeBytes)}</span>
                              </span>
                              <span className="project-files-admin-file__review-hint">
                                {reviewingFileId === file.id ? "Opening preview..." : "Open file for review"}
                              </span>
                            </button>
                            <IconButton
                              type="button"
                              className="project-files-admin-file__delete"
                              variant="danger"
                              size="sm"
                              icon={<Trash2 aria-hidden="true" />}
                              aria-label={`Delete ${file.fileName}`}
                              title="Delete file"
                              onClick={() => deleteFile(file)}
                              disabled={busy}
                            />
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </section>
                ))}
              </div>
            ) : (
              <EmptyState
                icon={<FolderOpen size={28} />}
                title="No project files registered"
                description="Uploaded files will appear here."
              />
            )}
          </div>
        </aside>

        <div className="project-files-admin__workspace">
          <section className="project-files-admin__upload" aria-label="Project file upload">
            <label
              className={`project-files-dropzone${dragActive ? " project-files-dropzone--active" : ""}`}
              onDragOver={(event) => {
                event.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={() => setDragActive(false)}
              onDrop={handleDrop}
            >
              <input type="file" multiple onChange={handleFileChange} disabled={busy} />
              <span className="project-files-dropzone__icon" aria-hidden="true"><UploadCloud size={28} /></span>
              <span className="project-files-dropzone__title">
                {selectedFiles.length ? `${selectedFiles.length} file${selectedFiles.length === 1 ? "" : "s"} selected` : "Choose or drop project files"}
              </span>
              <span className="project-files-dropzone__meta">
                {selectedFiles.length
                  ? selectedFileMeta
                  : "Project number can be detected from the file name."}
              </span>
            </label>

            <div className="project-files-admin__controls">
              <label>
                Package
                <select value={packageType} onChange={(event) => setPackageType(event.target.value as ProjectFilePackageType)} disabled={busy}>
                  {PACKAGE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </label>
              <label>
                Revision date
                <input type="date" value={revisionDate} onChange={(event) => setRevisionDate(event.target.value)} disabled={busy} />
              </label>
            </div>
          </section>

          <section className="project-files-admin__section project-files-admin__section--match" aria-label="Project match">
            <div className="project-files-admin__section-header">
              <div>
                <h3>Project match</h3>
                <p>{selectedProject ? `${projectTitle(selectedProject)} · ${projectAddress(selectedProject)}` : "No project selected."}</p>
              </div>
              {selectedProject ? <StatusBadge status="selected">{projectTitle(selectedProject)}</StatusBadge> : null}
            </div>

            <form className="project-files-admin-search" onSubmit={searchProjects}>
              <input
                aria-label="Search projects for project file upload"
                value={projectQuery}
                onChange={(event) => setProjectQuery(event.target.value)}
                placeholder="Project number, name, suburb, or street address"
                disabled={busy}
              />
              <Button type="submit" disabled={busy}>
                <Search size={16} aria-hidden="true" />
                {stage === "searching" ? "Searching" : "Search"}
              </Button>
            </form>

            {projectOptions.length ? (
              <div className="project-files-project-grid">
                {projectOptions.map((project) => (
                  <button
                    key={project.projectId}
                    type="button"
                    className={`project-files-project-card${selectedProject?.projectId === project.projectId ? " project-files-project-card--selected" : ""}`}
                    onClick={() => selectProject(project)}
                    disabled={busy}
                  >
                    <span>{projectTitle(project)}</span>
                    <strong>{project.projectName || "Unnamed project"}</strong>
                    <small>{projectAddress(project)}</small>
                  </button>
                ))}
              </div>
            ) : (
              <EmptyState
                icon={<FolderOpen size={28} />}
                title={debugProjectNumber ? `Project ${debugProjectNumber} ready to search` : "Project match pending"}
                description="Search results will appear here."
              />
            )}
          </section>

          <section className="project-files-admin__section project-files-admin__section--confirm" aria-label="Upload confirmation">
            <div>
              <h3>Upload target</h3>
              <p>{selectedProject ? `${projectTitle(selectedProject)} · ${PACKAGE_OPTIONS.find((option) => option.value === packageType)?.label} · ${revisionDate}` : "No project selected yet."}</p>
            </div>
            <div className="project-files-admin__actions">
              <Button type="button" variant="secondary" onClick={() => loadRegisteredFiles(inventoryScope === "project" ? selectedProject?.projectId || null : null, { scope: inventoryScope })} disabled={busy}>
                <RefreshCw size={16} aria-hidden="true" />
                Refresh
              </Button>
              <Button type="button" onClick={uploadProjectFiles} disabled={!canUpload}>
                <UploadCloud size={16} aria-hidden="true" />
                {stage === "uploading" ? "Uploading" : "Upload"}
              </Button>
            </div>
          </section>

          {feedback ? (
            <div className={`company-ai-feedback company-ai-feedback--${feedbackTone === "error" ? "error" : "success"}`}>
              {feedbackTone === "success" ? <CheckCircle2 size={16} aria-hidden="true" /> : null}
              <span>{feedback}</span>
            </div>
          ) : null}

          {lastUpload ? (
            <div className="project-files-admin__registered">
              <div>
                <span>Last registered file</span>
                <strong>{lastUpload.fileName}</strong>
              </div>
              <div className="company-ai-source-strip">
                <span className="company-ai-source-pill">{lastUpload.projectLabel}</span>
                <span className="company-ai-source-pill">{lastUpload.packageLabel}</span>
                <span className="company-ai-source-pill">{lastUpload.revisionDate}</span>
              </div>
            </div>
          ) : null}
        </div>

        <RecentAccessLogPane
          logs={accessLogs}
          loading={accessLogsLoading}
          error={accessLogsError}
          onRefresh={() => void loadAccessLogs()}
          tone="project-files"
          subtitle="Latest portal, download, and viewer activity"
        />
      </div>
    </Card>
  );
}
